import { Auth } from "aws-amplify";
import type { UserAttributes } from "hooks/authentication";
import { logError } from "../datadog";

/**
 * Authentication Service - Encapsulates AWS Amplify Auth operations
 * Benefits:
 * - Centralized authentication logic
 * - Easier testing and mocking
 * - Consistent error handling
 * - Separation from React components/hooks
 */

/**
 * CX-3151: Amplify rejects `currentSession()` with "No current user" for every
 * visitor who simply isn't signed in. Color Finder is browsable anonymously, so
 * this is the expected steady state — not a failure. Reporting it drowned the
 * real auth errors in RUM (748 events / 91 sessions over 14 days, ~70% of them
 * from sessions that never attempted a login).
 */
const isNoAuthenticatedUserError = (error: unknown): boolean => {
  const message =
    typeof error === "string"
      ? error
      : error instanceof Error
        ? error.message
        : "";
  return /no current user|not authenticated/i.test(message);
};

// biome-ignore lint/complexity/noStaticOnlyClass: ToBeEvaluated with the team
export class AuthService {
  /**
   * Get the current JWT access token
   */
  static async getAccessToken(): Promise<string | undefined> {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().getJwtToken();
    } catch (error) {
      if (!isNoAuthenticatedUserError(error)) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          source: "AuthService.getAccessToken",
        });
      }
      return undefined;
    }
  }

  /**
   * Check if all configured scopes are present in the token
   */
  static hasAllScopes(
    scopes: string | undefined,
    configuredScopes: Array<string> | string | undefined,
  ): boolean {
    const configuredScopesArray: Array<string> = configuredScopes
      ? Array.isArray(configuredScopes)
        ? configuredScopes
        : [configuredScopes]
      : [];
    return configuredScopesArray.every((configuredScope) =>
      scopes?.includes(configuredScope),
    );
  }

  /**
   * Check if the current session is valid
   */
  static async hasValidSession(): Promise<boolean> {
    try {
      const session = await Auth.currentSession();
      const isValid = session.isValid();
      const hasScopes = AuthService.hasAllScopes(
        session.getAccessToken().decodePayload().scope,
        Auth.configure()?.oauth?.scope,
      );
      return isValid && hasScopes;
    } catch (_error) {
      return false;
    }
  }

  /**
   * Get the currently authenticated user's attributes
   */
  static async getSignedInUser(): Promise<UserAttributes | undefined> {
    try {
      const user = await Auth.currentAuthenticatedUser();
      return user?.attributes;
    } catch (_error) {
      return undefined;
    }
  }

  /**
   * Initiate federated login with a custom provider
   */
  static async federatedSignIn(
    customProvider: string,
    customState?: string,
  ): Promise<any> {
    return Auth.federatedSignIn({
      customProvider,
      customState,
    });
  }
}
